import React, { useState } from "react";
import PropTypes from "prop-types";
import { ArrowDown, ArrowUp, Button, Checkbox, Plus, Select, Textarea, TextInput, Trash01 } from "@opendate-app/setlist";

import {
  CHOICE_QUESTION_TYPES,
  MAX_QUESTIONS,
  PLACEHOLDER_QUESTION_TYPES,
  QUESTION_TYPES,
  newQuestionKey,
} from "../signupFormFields";

const cleanOptions = (raw) => [...new Set(raw.split("\n").map((o) => o.trim().replace(/\s+/g, " ")).filter(Boolean))];

const CustomQuestionsEditor = ({ questions = [], onChange, isLive = false }) => {
  const [optionDrafts, setOptionDrafts] = useState({});

  const update = (key, patch) => onChange(questions.map((q) => (q.key === key ? { ...q, ...patch } : q)));

  const changeType = (key, type) => onChange(questions.map((q) => {
    if (q.key !== key) return q;
    const { options, ...rest } = q;
    if (!CHOICE_QUESTION_TYPES.includes(type)) return { ...rest, type };
    const draft = optionDrafts[key];
    return { ...rest, type, options: draft != null ? cleanOptions(draft) : options || [] };
  }));

  const changeOptions = (key, raw) => {
    setOptionDrafts((drafts) => ({ ...drafts, [key]: raw }));
    update(key, { options: cleanOptions(raw) });
  };

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= questions.length) return;
    const next = [...questions];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const remove = (key) => {
    if (isLive && !window.confirm("Delete this question? Past answers stay in submissions.")) return;
    onChange(questions.filter((q) => q.key !== key));
  };

  const add = () => onChange([...questions, { key: newQuestionKey(), type: "short_text", label: "", required: false }]);

  return (
    <div className="sf-questions">
      {questions.map((q, index) => {
        const options = q.options || [];
        return (
          <div key={q.key} className="sf-field-toggle">
            <div className="sf-question-head">
              <span className="sf-field-toggle-title">{`Question ${index + 1}`}</span>
              <div className="sf-question-actions">
                <Button variant="tertiary" size="xs" icon={ArrowUp} aria-label="Move question up" title="Move up"
                  data-test={`move-up-${q.key}`} disabled={index === 0} onClick={() => move(index, -1)} />
                <Button variant="tertiary" size="xs" icon={ArrowDown} aria-label="Move question down" title="Move down"
                  data-test={`move-down-${q.key}`} disabled={index === questions.length - 1} onClick={() => move(index, 1)} />
                <Button variant="tertiary" size="xs" icon={Trash01} aria-label="Delete question" title="Delete question"
                  data-test={`delete-${q.key}`} onClick={() => remove(q.key)} />
              </div>
            </div>
            <div className="sf-field-toggle-detail">
              <TextInput size="sm" label="Question" name={`${q.key}-label`} value={q.label || ""} maxLength={255}
                error={(q.label || "").trim() ? undefined : "Add a question"}
                onChange={(e) => update(q.key, { label: e.target.value })} />
              <Select size="sm" label="Type" id={`${q.key}-type`} value={q.type}
                options={QUESTION_TYPES} onChange={(type) => changeType(q.key, type)} />
              {PLACEHOLDER_QUESTION_TYPES.includes(q.type) && (
                <div className="sf-span-all">
                  <TextInput size="sm" label="Placeholder (optional)" name={`${q.key}-placeholder`} value={q.placeholder || ""} maxLength={255}
                    onChange={(e) => update(q.key, { placeholder: e.target.value })} />
                </div>
              )}
              {CHOICE_QUESTION_TYPES.includes(q.type) && (
                <div className="sf-span-all">
                  <Textarea size="sm" label="Options" name={`${q.key}-options`} rows={4} placeholder="One option per line"
                    value={optionDrafts[q.key] ?? options.join("\n")}
                    error={options.length < 2 ? "Add at least two options" : undefined}
                    hint="One option per line."
                    onChange={(e) => changeOptions(q.key, e.target.value)} />
                </div>
              )}
              <div className="sf-span-all">
                <Checkbox size="sm" label="Required" name={`${q.key}-required`} checked={!!q.required}
                  onChange={(e) => update(q.key, { required: e.target.checked })} />
              </div>
            </div>
          </div>
        );
      })}
      {questions.length < MAX_QUESTIONS && (
        <div>
          <Button variant="link-color" size="sm" icon={Plus} data-test="add-question" onClick={add}>Add question</Button>
        </div>
      )}
    </div>
  );
};

CustomQuestionsEditor.propTypes = {
  questions: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    required: PropTypes.bool,
    options: PropTypes.arrayOf(PropTypes.string),
  })),
  onChange: PropTypes.func.isRequired,
  isLive: PropTypes.bool,
};

export default CustomQuestionsEditor;
